import React, { useContext, useState } from 'react';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../routes/Routes';
import { Launch } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Startable, StartConditionType, validateStartCondition } from '../../utility/startCondition';

export interface DependencyOption {
  name: string;
  uuid: string;
}

const CONDITION_OPTIONS: { label: string; value: StartConditionType }[] = [
  { label: 'Manual', value: 'manual' },
  { label: 'Date', value: 'date' },
  { label: 'Offset', value: 'offset' },
  { label: 'Dependency', value: 'dependency' },
];

export interface IStartConditionProps<T extends Startable> {
  task: T;
  setTask: (task: T) => void;
  dependencyOptions: DependencyOption[];
  disabledConditions?: StartConditionType[];
  showLink?: boolean;
  conditionType: StartConditionType;
  onTypeChange: (type: StartConditionType) => void;
}

export default function StartCondition<T extends Startable>({
  task,
  setTask,
  dependencyOptions,
  disabledConditions = [],
  showLink = false,
  conditionType,
  onTypeChange,
}: IStartConditionProps<T>) {
  const { disabled } = useContext(DisableContext);
  const [touched, setTouched] = useState(false);

  const touchAndSet = (patch: Partial<Startable>) => {
    setTask({ ...task, ...patch });
    setTouched(true);
  };

  const toNumberOrNull = (v: string) => (v ? Number(v) : null);

  const buildResetValues = (value: StartConditionType): Pick<Startable, 'start_date' | 'depends_on_uuid' | 'start_date_offset'> => ({
    start_date: value === 'date' ? '' : null,
    depends_on_uuid: value === 'dependency' ? '' : null,
    start_date_offset: value === 'offset' ? 0 : null,
  });

  const handleTypeChange = (value: StartConditionType | undefined) => {
    if (!value) return;
    setTouched(false);
    onTypeChange(value);
    setTask({ ...task, ...buildResetValues(value) });
  };

  const { valid, missingField } = validateStartCondition(task, conditionType);
  const showError = touched && !valid;

  const availableOptions = _.reject(CONDITION_OPTIONS, (option) => _.includes(disabledConditions, option.value)) || [];
  const dependencyTasks = _.reject(dependencyOptions, { uuid: task.uuid || '' });

  return (
    <>
      <Autocomplete
        options={availableOptions}
        onChange={(_, option) => handleTypeChange(option?.value)}
        getOptionLabel={(option) => option.label}
        value={_.find(availableOptions, { value: conditionType }) || null}
        disabled={disabled}
        fullWidth
        renderInput={(params) => <TextField {...params} label="Start Condition" />}
      />

      {conditionType === 'date' && (
        <Tooltip title="This task will become ready on the selected date" placement="right">
          <TextField
            label="Start Date"
            type="date"
            onChange={(e) => touchAndSet({ start_date: e.target.value })}
            value={task.start_date ?? ''}
            disabled={disabled}
            error={showError && missingField === 'start_date'}
            helperText={showError && missingField === 'start_date' ? 'Start date is required' : undefined}
          />
        </Tooltip>
      )}

      {conditionType === 'offset' && (
        <Tooltip title="This task will become ready this many days relative to the event start date" placement="right">
          <TextField
            label="Offset days"
            type="number"
            onChange={(e) => touchAndSet({ start_date_offset: toNumberOrNull(e.target.value) })}
            value={task.start_date_offset ?? ''}
            disabled={disabled}
            error={showError && missingField === 'start_date_offset'}
            helperText={showError && missingField === 'start_date_offset' ? 'Offset is required' : undefined}
          />
        </Tooltip>
      )}

      {conditionType === 'dependency' && (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="This task will become ready when the selected task is completed" placement="right">
            <Autocomplete<DependencyOption>
              fullWidth
              options={dependencyTasks}
              getOptionLabel={(option) => option.name}
              value={_.find(dependencyTasks, { uuid: task.depends_on_uuid || '' }) || null}
              onChange={(_, option) => touchAndSet({ depends_on_uuid: option?.uuid || null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Depends On Task"
                  error={showError && missingField === 'depends_on_uuid'}
                  helperText={showError && missingField === 'depends_on_uuid' ? 'Dependency task is required' : undefined}
                />
              )}
            />
          </Tooltip>

          {showLink && task.depends_on_uuid && (
            <Link to={`/${ROUTE_PATHS.TASKS}/${task.depends_on_uuid}`} style={{ alignContent: 'center' }}>
              <Launch sx={{ color: 'black' }} />
            </Link>
          )}
        </Box>
      )}
    </>
  );
}
